import React, { useEffect, useState, useCallback } from "react";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLeadsData,
  fetchConvertedLeadsData,
  fetchFilteredLeadsData,
  fetchFilteredConvertedLeadsData,
  fetchNonConvertedLeadsData,
  fetchFilteredNonConvertedLeadsData,
} from "../../../redux/slice/leadsSlice";
import { setLeadUpdatedOn } from "../../../redux/slice/updatedOnLeadSlice";
import { downloadLeadsReport } from "../../../redux/slice/downloadLeadsSlice.js";
import {fetchMasterData} from "../../../redux/slice/masterSlice"
import { HashLoader } from "react-spinners";
import {
  Card,
  Button,
  Input,
  Select,
  Space,
  Alert,
  Empty,
  Collapse,
} from "antd";
import { StyledTable, StyledButton } from "../components/styleComp";
import { useAuth } from "../../../context/AuthContext";
import jwt_decode from "jwt-decode";
import LeadsSummary from "./LeadsSummary";
import { background } from "@chakra-ui/react";

const { Option } = Select;
const { Panel } = Collapse;

const Leads = () => {
  const { authState, onLogout } = useAuth();

  var token_decoded = authState.token;
  var jwtBearer = authState.token;
  var jwt_payload = "";
  var jwt_payload_group = [];
  var jwt_payload_expiry = [];

  if (jwtBearer) {
    // console.log("Token decoded");
    jwt_payload = jwt_decode(jwtBearer);
    jwt_payload_group = jwt_payload.group_id;
    jwt_payload_expiry = jwt_payload.exp;
    // console.log("JWT Group Reports - ", jwt_payload_group);
  }

  // Session Timeout
  const TOKEN_KEY = "my-jwt";
  var currentTime = Math.floor(Date.now() / 1000);

  if (jwt_payload_expiry.length !== 0) {
    if (currentTime > jwt_payload_expiry) {
      console.log("In token expired condition");
      AsyncStorage.removeItem(TOKEN_KEY);
      onLogout();
      location.reload();
    }
  }

  const dispatch = useDispatch();

  const [state, setState] = useState({
    pages: {
      leads: { page: 1, pageSize: 7 },
      convertedLeads: { page: 1, pageSize: 7 },
      nonConvertedLeads: { page: 1, pageSize: 7 },
    },
    filters: { leads: {}, convertedLeads: {}, nonConvertedLeads: {} },
    searchInputs: { leads: "", convertedLeads: "", nonConvertedLeads: "" },
    selectedColumns: { leads: "", convertedLeads: "", nonConvertedLeads: "" },
    hasSearched: {
      leads: false,
      convertedLeads: false,
      nonConvertedLeads: false,
    },
  });
  const [loadingStates, setLoadingStates] = useState({
    leads: false,
    convertedLeads: false,
    nonConvertedLeads: false,
  });

  const [triggerFilter, setTriggerFilter] = useState(null);

  useEffect(() => {
    if (jwtBearer) {
      const { page, pageSize } = state.pages.leads;
      dispatch(
        fetchLeadsData({
          jwtBearer,
          page,
          filters: state.filters.leads,
          pageSize,
        })
      );
    }
  }, [jwtBearer, state.pages.leads, state.filters.leads, dispatch]);

  useEffect(() => {
    if (jwtBearer) {
      const { page, pageSize } = state.pages.convertedLeads;
      dispatch(
        fetchConvertedLeadsData({
          jwtBearer,
          page,
          filters: state.filters.convertedLeads,
          pageSize,
        })
      );
    }
  }, [
    jwtBearer,
    state.pages.convertedLeads,
    state.filters.convertedLeads,
    dispatch,
  ]);

  useEffect(() => {
    if (jwtBearer) {
      const { page, pageSize } = state.pages.nonConvertedLeads;
      dispatch(
        fetchNonConvertedLeadsData({
          jwtBearer,
          page,
          filters: state.filters.nonConvertedLeads,
          pageSize,
        })
      );
    }
  }, [
    jwtBearer,
    state.pages.nonConvertedLeads,
    state.filters.nonConvertedLeads,
    dispatch,
  ]);

  const {
    leadsData,
    convertedLeadsData,
    nonConvertedLeadsData,
    filteredLeadsData,
    filteredConvertedLeadsData,
    nonFilteredConvertedLeadsData,
    totalCountLeads,
    totalCountConvertedLeads,
    totalCountNonConvertedLeads,
    isLoadingLeads,
    isLoadingConvertedLeads,
    isLoadingNonConvertedLeads,
    isLoadingFilteredLeads,
    isLoadingFilteredConvertedLeads,
    isLoadingNonFilteredConvertedLeads,
    errorLeads,
    errorConvertedLeads,
    errorNonConvertedLeads,
    updatedOn,
  } = useSelector((state) => state.leadsReports);

  // console.log("UpdatedOn", updatedOn);

  dispatch(setLeadUpdatedOn(updatedOn));

  const {
    isDownloadingLeads,
    isDownloadingConvertedLeads,
    isErrorLeadsDownload,
    isErrorConvertedLeadsDownload,
  } = useSelector((state) => state.downloadLeads);

  // console.log("isDownloadingConvertedLeads", isDownloadingConvertedLeads)

  const defineColumns = useCallback((data) => {
    if (data.length === 0) return [];
    const keys = Object.keys(data[0]);
    return keys.map((key) => ({
      title: (
        <strong style={{ fontSize: "18px" }}>
          {key.replace(/_/g, " ").toUpperCase()}
        </strong>
      ),
      dataIndex: key,
      key: key,
      render: (text) => <span style={{ fontSize: "16px" }}>{text}</span>,
    }));
  }, []);

  const handleKeyDown = (event, type) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleFilter(type);
    }
  };

  const handleFilter = (type) => {
    trackFeatureAction("Reports", "Leads", "search");
    const filterActions = {
      leads: fetchFilteredLeadsData,
      convertedLeads: fetchFilteredConvertedLeadsData,
      nonConvertedLeads: fetchFilteredNonConvertedLeadsData,
    };

    const columnName = state.selectedColumns[type].toLowerCase();
    const trimmedInput = state.searchInputs[type].trim();

    dispatch(
      filterActions[type]({
        jwtBearer,
        filters: { [columnName]: trimmedInput },
      })
    );

    setState((prevState) => ({
      ...prevState,
      hasSearched: {
        ...prevState.hasSearched,
        [type]: true,
      },

      pages: {
        ...prevState.pages,
        [type]: { ...prevState.pages[type], page: 1 },
      },
    }));
  };

  const handleReset = (type) => {
    trackFeatureAction("Reports", "Leads", "reset");
    setState((prevState) => ({
      ...prevState,
      searchInputs: { ...prevState.searchInputs, [type]: "" },
      selectedColumns: { ...prevState.selectedColumns, [type]: "" },
      hasSearched: { ...prevState.hasSearched, [type]: false },
      filters: { ...prevState.filters, [type]: {} },
    }));

    const { page, pageSize } = state.pages[type];

    // dispatch 3 thunks
    dispatch(
      (type === "leads"
        ? fetchLeadsData
        : type === "convertedLeads"
        ? fetchConvertedLeadsData
        : fetchNonConvertedLeadsData)({
        jwtBearer,
        page,
        filters: {},
        pageSize,
      })
    );
  };

  useEffect(() => {
    if (triggerFilter) {
      handleFilter(triggerFilter);
      setTriggerFilter(null);
    }
  }, [triggerFilter]);

  const paginationConfig = (type) => {
    
    const total = {
  leads: state.hasSearched.leads ? filteredLeadsData.length : totalCountLeads,
  convertedLeads: state.hasSearched.convertedLeads 
    ? filteredConvertedLeadsData.length 
    : totalCountConvertedLeads,
  nonConvertedLeads: state.hasSearched.nonConvertedLeads
    ? nonFilteredConvertedLeadsData.length
    : totalCountNonConvertedLeads,
}[type];

    return {
      current: state.pages[type].page,
      pageSize: state.pages[type].pageSize,
      total,
      showTotal: (total, [start, end]) => `${start}-${end} of ${total} items`,
      onChange: (page, pageSize) =>
        setState((prevState) => ({
          ...prevState,
          pages: { ...prevState.pages, [type]: { page, pageSize } },
        })),
    };
  };

  const renderSearchBar = (columns, type) => (
    <Space style={{ marginBottom: 16, marginLeft: 2 }}>
      <Select
        placeholder="Select Column"
        style={{ width: 200, height: 32, lineHeight: "32px" }}
        value={state.selectedColumns[type] || undefined}
        onChange={(value) =>
          setState((prevState) => ({
            ...prevState,
            selectedColumns: {
              ...prevState.selectedColumns,
              [type]: value,
            },
          }))
        }
      >
        {columns.map((col) => (
          <Option key={col.key} value={col.dataIndex}>
            {col.title}
          </Option>
        ))}
      </Select>
      <Input
        placeholder="Value"
        value={state.searchInputs[type]}
        onChange={(e) =>
          setState((prevState) => ({
            ...prevState,
            searchInputs: {
              ...prevState.searchInputs,
              [type]: e.target.value,
            },
          }))
        }
        onKeyDown={(e) => handleKeyDown(e, type)}
        style={{ width: 150, height: 32, lineHeight: "32px" }}
        disabled={!state.selectedColumns[type]}
      />
      <Button
        type="primary"
        onClick={() => handleFilter(type)}
        style={{ height: 32, lineHeight: "32px" }}
      >
        Search
      </Button>
      {state[type] && ( // Show reset button only if filtered is true
        <Button
          onClick={() => handleReset(type)}
          style={{ width: 70, height: 32, lineHeight: "32px" }}
        >
          Reset
        </Button>
      )}
    </Space>
  );

  // const handleAPIDownload = (type) => {
  //   dispatch(downloadLeadsReport({ jwtBearer, type }));
  // };

  const handleAPIDownload = async (type) => {
    trackFeatureAction("Reports", "Leads", "download");
    try {
      setLoadingStates((prev) => ({ ...prev, [type]: true }));

      await dispatch(downloadLeadsReport({ jwtBearer, type }));
    } catch (error) {
      console.error("Error downloading report:", error);
      alert("Failed to download report. Please try again.");
    } finally {
      setLoadingStates((prev) => ({ ...prev, [type]: false }));
    }
  };

  const downloadButton = (type) => (
    <StyledButton
      type="primary"
      onClick={() => handleAPIDownload(type)}
      loading={loadingStates[type]}
    >
      Download Report
    </StyledButton>
  );

  return (
    <div>
      {jwt_payload_group === "3" ||
      jwt_payload_group === "4" ||
      jwt_payload_group === "5" ? null : (
        <LeadsSummary />
      )}
      <Collapse style={{ padding: "1rem", marginBottom: "2rem" }}>
        {["leads", "convertedLeads"].map((type) => {
        // {["leads", "convertedLeads", "nonConvertedLeads"].map((type) => {
          const data = state.hasSearched[type]
            ? type === "leads"
              ? filteredLeadsData
              : type === "convertedLeads"
              ? filteredConvertedLeadsData
              : nonFilteredConvertedLeadsData
            : type === "leads"
            ? leadsData
            : type === "convertedLeads"
            ? convertedLeadsData
            : nonConvertedLeadsData;
          const columns = defineColumns(data || []);
          const isLoading = {
            leads: isLoadingLeads,
            convertedLeads: isLoadingConvertedLeads,
            nonConvertedLeads: isLoadingNonConvertedLeads,
          }[type];
          const error = {
            leads: errorLeads,
            convertedLeads: errorConvertedLeads,
            nonConvertedLeads: errorNonConvertedLeads,
          }[type];
          const isLoadingFiltered = {
            leads: isLoadingFilteredLeads,
            convertedLeads: isLoadingFilteredConvertedLeads,
            nonConvertedLeads: isLoadingNonFilteredConvertedLeads,
          }[type];

          return (
            <Panel
              header={
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    backgroundColor: "#EEEEEE",
                    color: "black",
                    // padding: "1rem 0rem",
                  }}
                >
                  <span
                    style={{
                      fontSize: "20px",
                      color: "black",
                      fontFamily: "Poppins",
                    }}
                  >
                    {type === "leads"
                      ? "LEAD PIPELINE"
                      : type === "convertedLeads"
                      ? "CONVERTED LEAD"
                      : "NON-CONVERTED LEADS"}
                  </span>{" "}
                  <span
                    className="expand-text"
                    style={{
                      color: "black",
                      fontSize: "18px",
                      fontFamily: "Helvetica",
                      marginLeft: "10px",
                    }}
                  >
                    (Click here to expand or collapse)
                  </span>
                </div>
              }
              key={type}
              style={{
                backgroundColor: "#ecf0f1",
                marginBottom: "1rem",
                border: "1px solid #bdc3c7",
                borderRadius: "8px",
              }}
            >
              <Card
                key={type}
                title={
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: "24px",
                      fontFamily: "Poppins, sans-serif",
                      color: "#34495e",
                    }}
                  >
                    {
                      type === "leads"
                        ? "LEAD PIPELINE"
                        : type === "convertedLeads"
                        ? "CONVERTED LEAD"
                        : "NON-CONVERTED LEADS"
                    }
                  </span>
                }
                bordered
                extra={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {downloadButton(type)}
                  </div>
                }
              >
                {renderSearchBar(columns, type)}

                {isLoadingFiltered || isLoading ? (
                  <div className="spinner-container">
                    <HashLoader color="#f57171" />
                  </div>
                ) : error ? (
                  <Alert message={error} type="error" />
                ) : (
                  <StyledTable
                    columns={columns}
                    dataSource={data}
                    pagination={paginationConfig(type)}
                    locale={{
                      emptyText: <Empty description="No data available" />,
                    }}
                  />
                )}
              </Card>
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
};

export default Leads;
